import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';

import StaticAdsService from 'services/StaticAdsService';
import { adsetUrl } from 'pages/AdLaunch/adsManager';
import { downloadBlob } from 'shared/utils/downloadBlob';

import {
  acceptedCount,
  INSPO_SET_STATUS_LABEL,
  launchSummary,
  pairSlots,
  shownImage,
} from './inspoSets';
import { InspoSet, InspoSetSlot } from './types';

// The creative agent's daily ad sets: one competitor ad used as inspiration,
// remade for DCOP as variations A–E, each a feed image and a 9:16 twin, with a
// Meta headline + caption per variation. Shown above the Output grid; the same
// images also appear in the grid as ordinary generations.

const STATUS_COLOR: Record<
  InspoSet['status'],
  'default' | 'success' | 'warning'
> = {
  GENERATING: 'default',
  READY: 'success',
  SHORT: 'warning',
};

function CopyButton({ text, label }: { text: string | null; label: string }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <Tooltip title={copied ? 'Copied' : `Copy ${label}`}>
      <IconButton
        size="small"
        onClick={() => {
          void navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
      >
        <ContentCopyIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}

function SlotImage({ slot }: { slot: InspoSetSlot | null }) {
  if (!slot) return <Box sx={{ width: 120 }} />;
  const { image, accepted } = shownImage(slot);
  const tall = slot.aspectRatio === '9:16';
  return (
    <Box sx={{ width: tall ? 90 : 120 }}>
      <Box
        sx={{
          width: '100%',
          aspectRatio: slot.aspectRatio.replace(':', ' / '),
          bgcolor: 'grey.100',
          borderRadius: 1,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          outline: accepted ? 'none' : '1px dashed',
          outlineColor: 'divider',
        }}
      >
        {image.imageUrl ? (
          <Box
            component="a"
            href={image.imageUrl}
            target="_blank"
            rel="noreferrer"
            sx={{ display: 'block', width: '100%', height: '100%' }}
          >
            <Box
              component="img"
              src={image.imageUrl}
              alt={`${slot.variation} ${slot.aspectRatio}`}
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        ) : image.status === 'FAILED' ? (
          <Typography
            variant="caption"
            color="error"
            sx={{ p: 0.5, textAlign: 'center' }}
          >
            Failed
          </Typography>
        ) : (
          <CircularProgress size={18} />
        )}
      </Box>
      <Typography
        variant="caption"
        color={accepted ? 'text.secondary' : 'warning.main'}
        sx={{ display: 'block' }}
      >
        {slot.aspectRatio}
        {accepted ? '' : ' · not accepted'}
        {slot.attempts.length > 1 ? ` · try ${slot.attempts.length}` : ''}
      </Typography>
    </Box>
  );
}

function SetCard({
  set,
  defaultExpanded,
  service,
}: {
  set: InspoSet;
  defaultExpanded: boolean;
  service: StaticAdsService;
}) {
  const pairs = useMemo(() => pairSlots(set.slots), [set.slots]);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accepted = acceptedCount(set);
  const launch = set.launch ? launchSummary(set.launch) : null;

  const download = async () => {
    setDownloading(true);
    setError(null);
    try {
      const blob = await service.getInspoSetZip(set.setId);
      downloadBlob(blob, `dcop_${set.runDate}_${set.setId.slice(0, 6)}.zip`);
    } catch (err: any) {
      // A blob error body has to be read before its message is usable.
      let message = err?.message || 'Download failed';
      if (err?.response?.data instanceof Blob) {
        try {
          message = JSON.parse(await err.response.data.text()).error || message;
        } catch {
          // not JSON: keep the request's own message
        }
      }
      setError(message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      variant="outlined"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ width: '100%', pr: 1, minWidth: 0 }}
        >
          {set.inspo?.imageUrl && (
            <Box
              component="img"
              src={set.inspo.imageUrl}
              alt="Inspiration ad"
              sx={{
                width: 40,
                height: 40,
                objectFit: 'cover',
                borderRadius: 0.5,
              }}
            />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {set.runDate} · from {set.inspo?.pageName || 'a library ad'}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: 'block' }}
            >
              {accepted} of {set.slots.length} images ready · {set.renders}{' '}
              renders
            </Typography>
          </Box>
          {launch && (
            <Chip
              size="small"
              variant="outlined"
              color={launch.severity}
              label={launch.chip}
            />
          )}
          <Chip
            size="small"
            color={STATUS_COLOR[set.status]}
            label={INSPO_SET_STATUS_LABEL[set.status]}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ mb: 2 }}
          alignItems={{ md: 'flex-start' }}
        >
          <Box sx={{ flex: 1 }}>
            {set.pickReason && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Why this inspiration:</strong> {set.pickReason}
              </Typography>
            )}
            {set.landingUrl && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Send to:</strong>{' '}
                <Link href={set.landingUrl} target="_blank" rel="noreferrer">
                  {set.landingUrl.replace(/^https?:\/\//, '')}
                </Link>
                {set.landingReason ? ` (${set.landingReason})` : ''}
              </Typography>
            )}
            {set.notes && (
              <Alert
                severity={set.status === 'SHORT' ? 'warning' : 'info'}
                sx={{ mt: 1 }}
              >
                {set.notes}
              </Alert>
            )}
            {launch && (
              <Alert severity={launch.severity} sx={{ mt: 1 }}>
                <strong>{launch.label}</strong>
                {set.launch?.campaign ? ` in ${set.launch.campaign}` : ''}
                {set.launch?.accountId && set.launch.adsetId && (
                  <>
                    {' · '}
                    <Link
                      href={adsetUrl(
                        set.launch.accountId,
                        set.launch.adsetId,
                        set.launch.campaignId,
                      )}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open in Ads Manager
                    </Link>
                  </>
                )}
                {set.launch?.note ? `. ${set.launch.note}` : ''}
                {set.launch?.errors.length
                  ? ` Errors: ${set.launch.errors.join('; ')}`
                  : ''}
              </Alert>
            )}
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={
              downloading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DownloadIcon />
              )
            }
            disabled={downloading || accepted === 0}
            onClick={() => void download()}
          >
            Download zip
          </Button>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          {pairs.map(pair => (
            <Stack
              key={pair.variation}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'flex-start' }}
            >
              <Typography variant="h6" sx={{ width: 24, flexShrink: 0 }}>
                {pair.variation}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <SlotImage slot={pair.feed} />
                <SlotImage slot={pair.story} />
              </Stack>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Headline
                  </Typography>
                  <CopyButton text={pair.headline} label="headline" />
                </Stack>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  {pair.headline}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Caption
                  </Typography>
                  <CopyButton text={pair.caption} label="caption" />
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {pair.caption}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default function InspoSetsPanel() {
  const service = useMemo(() => new StaticAdsService(), []);
  const [sets, setSets] = useState<InspoSet[] | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const resp = await service.listInspoSets(6);
    setSets(resp ? resp.sets : null);
    setLoading(false);
  }, [service]);

  useEffect(() => {
    void load();
  }, [load]);

  // No sets yet (the agent hasn't run): stay out of the way of the Output grid.
  if (!loading && sets && !sets.length) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Daily inspiration sets
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          The creative agent&apos;s DCOP ads from competitor inspiration, each
          with its Meta headline and caption.
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton
              size="small"
              aria-label="Refresh daily inspiration sets"
              onClick={() => void load()}
              disabled={loading}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      {loading && !sets ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : !sets ? (
        <Alert severity="warning">
          Could not load the daily inspiration sets.
        </Alert>
      ) : (
        <Stack spacing={1}>
          {sets.map((set, i) => (
            <SetCard
              key={set.setId}
              set={set}
              defaultExpanded={i < 2}
              service={service}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
